import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Plus, ArrowUpRight } from 'lucide-react';
import { apiFetch } from '../../utils/api';

const ProjectsManagement: React.FC = () => {
    const [projects, setProjects] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [editData, setEditData] = useState<any>(null);
    const [showEditModal, setShowEditModal] = useState(false);

    // Create Modal State
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createData, setCreateData] = useState({
        project_name: '', stage: 'Propose', account_id: '', description: '',
        opportunity_type: 'Fix Price', initial_quotation: 0, final_quotation: 0,
        start_date: '', expected_delivery_date: '', technologies: '', discount: 0, resource_count: 0
    });
    const [accounts, setAccounts] = useState<any[]>([]); // For selecting account


    const fetchProjects = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch('/projects');
            if (res.ok) setProjects(await res.json());
        } catch (err) { console.error(err); } finally { setLoading(false); }
    }, []);

    const fetchAccounts = useCallback(async () => {
        try {
            const res = await apiFetch('/accounts');
            if (res.ok) setAccounts(await res.json());
        } catch (err) { console.error(err); }
    }, []);

    useEffect(() => {
        fetchProjects();
        fetchAccounts();
    }, [fetchProjects, fetchAccounts]);

    const handleCreateProject = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const payload = {
                ...createData,
                technologies: createData.technologies.split(',').map(t => t.trim())
            };
            const res = await apiFetch('/projects', {
                method: 'POST',
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                alert('Project created successfully');
                setShowCreateModal(false);
                setCreateData({
                    project_name: '', stage: 'Propose', account_id: '', description: '',
                    opportunity_type: 'Fix Price', initial_quotation: 0, final_quotation: 0,
                    start_date: '', expected_delivery_date: '', technologies: '', discount: 0, resource_count: 0
                });
                fetchProjects();
            } else {
                alert('Failed to create project');
            }
        } catch (err) {
            console.error(err);
            alert('Error creating project');
        }
    };

    const handleEditClick = async (projectId: string) => {
        setLoading(true);
        try {
            const res = await apiFetch(`/projects/${projectId}`);
            if (res.ok) {
                const data = await res.json();
                setEditData({
                    ...data,
                    technologies: data.technologies ? data.technologies.join(', ') : '',
                    description: data.description || ''
                });
                setShowEditModal(true);
            } else {
                alert('Failed to fetch project details');
            }
        } catch (err) {
            console.error(err);
            alert('Error fetching project details');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const payload = {
                ...editData,
                technologies: editData.technologies.split(',').map((t: string) => t.trim())
            };
            const res = await apiFetch(`/projects/${editData.id}`, {
                method: 'PUT',
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                alert('Project updated successfully');
                setShowEditModal(false);
                fetchProjects();
            } else {
                alert('Failed to update project');
            }
        } catch (err) {
            console.error(err);
            alert('Error updating project');
        }
    };

    return (
        <div className="space-y-8">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">Projects Management</h2>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                >
                    <Plus size={18} /> Add Project
                </button>
            </div>
            {loading && !showEditModal ? <Loader2 className="animate-spin" /> : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50/50">
                            <tr>
                                <th className="px-6 py-4">Project Name</th>
                                <th className="px-6 py-4">Account</th>
                                <th className="px-6 py-4">Stage</th>
                                <th className="px-6 py-4">Created At</th>
                                <th className="px-6 py-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {projects.map(p => (
                                <tr key={p.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 font-bold">{p.project_name}</td>
                                    <td className="px-6 py-4">{p.account_name}</td>
                                    <td className="px-6 py-4">
                                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-bold uppercase">{p.stage}</span>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500">{new Date(p.created_at).toLocaleDateString()}</td>
                                    <td className="px-6 py-4 flex gap-4">
                                        <Link to={`/admin/projects/${p.id}`} className="flex items-center gap-1 text-blue-600 font-bold hover:underline">
                                            Manage <ArrowUpRight size={14} />
                                        </Link>
                                        <button onClick={() => handleEditClick(p.id)} className="text-gray-400 font-semibold hover:text-blue-600">Quick Edit</button>
                                        <button
                                            onClick={async () => {
                                                if (window.confirm('Are you sure you want to delete this project?')) {
                                                    try {
                                                        const res = await apiFetch(`/projects/${p.id}`, {
                                                            method: 'DELETE'
                                                        });
                                                        if (res.ok) {
                                                            fetchProjects();
                                                            alert('Project deleted successfully');
                                                        } else {
                                                            alert('Failed to delete project');
                                                        }
                                                    } catch (err) {
                                                        console.error(err);
                                                        alert('Error deleting project');
                                                    }
                                                }
                                            }}
                                            className="text-red-600 font-semibold hover:underline"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && editData && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-2xl font-bold text-gray-800">Project Details: {editData.project_name}</h3>
                            <button onClick={() => setShowEditModal(false)} className="text-gray-500 hover:text-gray-700">Close</button>
                        </div>
                        <form onSubmit={handleUpdate} className="space-y-8">
                            {/* Project Info */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-sm font-semibold text-gray-700">Project Name</label>
                                    <input className="w-full border p-2 rounded" value={editData.project_name} onChange={e => setEditData({ ...editData, project_name: e.target.value })} />
                                </div>
                                <div className="space-y-1 md:col-span-2">
                                    <label className="text-sm font-semibold text-gray-700">Description</label>
                                    <textarea className="w-full border p-2 rounded" rows={3} value={editData.description} onChange={e => setEditData({ ...editData, description: e.target.value })} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-semibold text-gray-700">Stage</label>
                                    <select className="w-full border p-2 rounded" value={editData.stage} onChange={e => setEditData({ ...editData, stage: e.target.value })}>
                                        <option value="Propose">Propose</option>
                                        <option value="Develop">Develop</option>
                                        <option value="Support">Support</option>
                                        <option value="Enhancement">Enhancement</option>
                                        <option value="Close">Close</option>
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-semibold text-gray-700">Type</label>
                                    <select className="w-full border p-2 rounded" value={editData.opportunity_type} onChange={e => setEditData({ ...editData, opportunity_type: e.target.value })}>
                                        <option value="Fix Price">Fix Price</option>
                                        <option value="T&M">Time & Material</option>
                                        <option value="Manage Service">Managed Service</option>
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-semibold text-gray-700">Technologies</label>
                                    <input className="w-full border p-2 rounded" value={editData.technologies} onChange={e => setEditData({ ...editData, technologies: e.target.value })} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-semibold text-gray-700">Resource Count</label>
                                    <input type="number" className="w-full border p-2 rounded" value={editData.resource_count} onChange={e => setEditData({ ...editData, resource_count: parseInt(e.target.value) })} />
                                </div>
                            </div>

                            {/* Commercials */}
                            <h4 className="font-bold text-lg border-b pb-2">Commercials & Timeline</h4>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="space-y-1 md:col-span-2">
                                    <label className="text-sm font-semibold text-gray-700">Start Date</label>
                                    <input type="date" className="w-full border p-2 rounded" value={editData.start_date || ''} onChange={e => setEditData({ ...editData, start_date: e.target.value })} />
                                </div>
                                <div className="space-y-1 md:col-span-2">
                                    <label className="text-sm font-semibold text-gray-700">Expected Delivery</label>
                                    <input type="date" className="w-full border p-2 rounded" value={editData.expected_delivery_date || ''} onChange={e => setEditData({ ...editData, expected_delivery_date: e.target.value })} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-semibold text-gray-700">Initial Quote</label>
                                    <input type="number" className="w-full border p-2 rounded" value={editData.initial_quotation} onChange={e => setEditData({ ...editData, initial_quotation: parseFloat(e.target.value) })} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-semibold text-gray-700">Final Quote</label>
                                    <input type="number" className="w-full border p-2 rounded" value={editData.final_quotation} onChange={e => setEditData({ ...editData, final_quotation: parseFloat(e.target.value) })} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-semibold text-gray-700">Discount</label>
                                    <input type="number" className="w-full border p-2 rounded" value={editData.discount} onChange={e => setEditData({ ...editData, discount: parseFloat(e.target.value) })} />
                                </div>
                            </div>

                            <div className="flex justify-end gap-4 pt-4">
                                <button type="button" onClick={() => setShowEditModal(false)} className="px-4 py-2 text-gray-600 font-bold hover:bg-gray-100 rounded-lg">Cancel</button>
                                <button type="submit" className="px-6 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700">Save Changes</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Create Project Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-2xl font-bold text-gray-800">Add New Project</h3>
                            <button onClick={() => setShowCreateModal(false)} className="text-gray-500 hover:text-gray-700">Close</button>
                        </div>
                        <form onSubmit={handleCreateProject} className="space-y-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-sm font-semibold text-gray-700">Account *</label>
                                    <select className="w-full border p-2 rounded" value={createData.account_id} onChange={e => setCreateData({ ...createData, account_id: e.target.value })} required>
                                        <option value="">Select Account</option>
                                        {accounts.map(acc => <option key={acc.id} value={acc.id}>{acc.name}</option>)}
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-semibold text-gray-700">Project Name *</label>
                                    <input className="w-full border p-2 rounded" value={createData.project_name} onChange={e => setCreateData({ ...createData, project_name: e.target.value })} required />
                                </div>
                                <div className="space-y-1 md:col-span-2">
                                    <label className="text-sm font-semibold text-gray-700">Description</label>
                                    <textarea className="w-full border p-2 rounded" rows={3} value={createData.description} onChange={e => setCreateData({ ...createData, description: e.target.value })} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-semibold text-gray-700">Stage</label>
                                    <select className="w-full border p-2 rounded" value={createData.stage} onChange={e => setCreateData({ ...createData, stage: e.target.value })}>
                                        <option value="Propose">Propose</option>
                                        <option value="Develop">Develop</option>
                                        <option value="Support">Support</option>
                                        <option value="Enhancement">Enhancement</option>
                                        <option value="Close">Close</option>
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-semibold text-gray-700">Type</label>
                                    <select className="w-full border p-2 rounded" value={createData.opportunity_type} onChange={e => setCreateData({ ...createData, opportunity_type: e.target.value })}>
                                        <option value="Fix Price">Fix Price</option>
                                        <option value="T&M">Time & Material</option>
                                        <option value="Manage Service">Managed Service</option>
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-semibold text-gray-700">Technologies</label>
                                    <input className="w-full border p-2 rounded" value={createData.technologies} onChange={e => setCreateData({ ...createData, technologies: e.target.value })} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-semibold text-gray-700">Resource Count</label>
                                    <input type="number" className="w-full border p-2 rounded" value={createData.resource_count} onChange={e => setCreateData({ ...createData, resource_count: parseInt(e.target.value) })} />
                                </div>
                            </div>

                            <h4 className="font-bold text-lg border-b pb-2">Commercials & Timeline</h4>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="space-y-1 md:col-span-2">
                                    <label className="text-sm font-semibold text-gray-700">Start Date</label>
                                    <input type="date" className="w-full border p-2 rounded" value={createData.start_date} onChange={e => setCreateData({ ...createData, start_date: e.target.value })} />
                                </div>
                                <div className="space-y-1 md:col-span-2">
                                    <label className="text-sm font-semibold text-gray-700">Expected Delivery</label>
                                    <input type="date" className="w-full border p-2 rounded" value={createData.expected_delivery_date} onChange={e => setCreateData({ ...createData, expected_delivery_date: e.target.value })} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-semibold text-gray-700">Initial Quote</label>
                                    <input type="number" className="w-full border p-2 rounded" value={createData.initial_quotation} onChange={e => setCreateData({ ...createData, initial_quotation: parseFloat(e.target.value) })} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-semibold text-gray-700">Final Quote</label>
                                    <input type="number" className="w-full border p-2 rounded" value={createData.final_quotation} onChange={e => setCreateData({ ...createData, final_quotation: parseFloat(e.target.value) })} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-semibold text-gray-700">Discount</label>
                                    <input type="number" className="w-full border p-2 rounded" value={createData.discount} onChange={e => setCreateData({ ...createData, discount: parseFloat(e.target.value) })} />
                                </div>
                            </div>

                            <div className="flex justify-end gap-4 pt-4">
                                <button type="button" onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-gray-600 font-bold hover:bg-gray-100 rounded-lg">Cancel</button>
                                <button type="submit" className="px-6 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700">Create Project</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectsManagement;
